import React, { useState } from 'react';
import { useFormikContext } from 'formik';
import { Input, Select } from 'syngenta-digital-cropwise-react-ui-kit';
import FormGrid, { FormRowConfig } from 'components/FormGrid';
import { COL_SPAN_ONE_THIRD } from 'components/FormGrid/constants';
import CropIconMapping from 'components/CropIconMapping';
import getDropdownParentWithoutDetaching from 'utilities/getDropdownParentWithoutDetaching';
import { normalizeString } from 'helpers/stringUtils';
import ChevronUp from 'assets/chevron_up.svg?react';
import ChevronDown from 'assets/chevron_down.svg?react';
import CurrencyInput from './CurrencyInput';
import applyPlannedAreaChange from '../helpers/applyPlannedAreaChange';
import { PlanWizardFormValues } from '../../../types';
import { useCropDataContext } from '../../CropDataContext';

/**
 * PLAN INFO section of the Details step: Plan ID, Planned Area, and Crop.
 */
const PlanInfoSection: React.FC = () => {
  const {
    values,
    errors,
    touched,
    handleChange,
    setFieldValue,
    setFieldTouched
  } = useFormikContext<PlanWizardFormValues>();

  // `crops` comes from `CropDataContext`, fetched once at the wizard-shell
  // level before this step ever mounts — no separate loading branch needed
  // here.
  const { crops } = useCropDataContext();
  const cropOptions = (crops ?? []).map(crop => ({
    label: (
      <div className="flex items-center">
        <CropIconMapping cropObject={crop} size={20} />
        <span className="ml-2">{crop.name}</span>
      </div>
    ),
    value: crop.id
  }));

  const [isCropDropdownOpen, setIsCropDropdownOpen] = useState(false);

  /**
   * Planned Area drives every Total<->Per Ac pair (Total = Per Ac x area).
   * Recomputing every Total from its Per Ac "rate" on area change (rather
   * than replicating a per-field asymmetric touched-field heuristic) keeps
   * the rate fields authoritative across an area edit — the simpler,
   * predictable rule.
   *
   * Also marks `estimatedAreaValue` touched, so PropertiesStep's Planned-Area
   * sync knows the user now owns this field and stops overwriting it.
   */
  const handleAreaCommit = (newArea: number) => {
    // `CurrencyInput` commits 0 for an empty/invalid edit — normalize back to
    // a positive fallback here, matching legacy `PlanInfoSection`'s own
    // `onPlannedAreaBlur` (which falls back to 1), so an emptied field can't
    // persist `area: 0` and break every Total<->Per Ac division below.
    const normalizedArea = newArea > 0 ? newArea : 1;
    setFieldTouched('area', true);
    setFieldTouched('estimatedAreaValue', true);
    applyPlannedAreaChange(values, setFieldValue, normalizedArea);
  };

  const planInfoRows: FormRowConfig[] = [
    {
      id: 'plan-info-row',
      showErrorLineConditionally: false,
      fields: [
        {
          id: 'name',
          // Intentionally renamed from "Plan Name" per design QA.
          label: 'Plan ID',
          colSpan: COL_SPAN_ONE_THIRD,
          required: true,
          error: touched.name ? errors.name : undefined,
          render: () => (
            <Input
              type="default"
              name="name"
              autoFocus
              data-testid="planName"
              value={values.name}
              onChange={handleChange}
              onBlur={() => setFieldTouched('name', true)}
              status={touched.name && errors.name ? 'error' : undefined}
            />
          )
        },
        {
          id: 'estimatedAreaValue',
          label: 'Planned Area',
          colSpan: COL_SPAN_ONE_THIRD,
          required: true,
          error: touched.area ? errors.area : undefined,
          render: () => (
            <CurrencyInput
              value={values.estimatedAreaValue}
              disabled={false}
              dataTestId="plannedArea"
              ariaLabel="Planned Area"
              prefix=""
              suffix={<span className="text-neutral-60">ac</span>}
              onCommit={handleAreaCommit}
            />
          )
        },
        {
          id: 'cropId',
          label: 'Crop',
          colSpan: COL_SPAN_ONE_THIRD,
          render: () => (
            <Select
              className="custom-select"
              data-testid="cropSelect"
              style={{ width: '100%' }}
              showSearch
              allowClear
              filterOption={(input, option) => {
                const crop = crops?.find(
                  cropOption => cropOption.id === option?.value
                );
                return (
                  !!crop &&
                  normalizeString(crop.name).includes(normalizeString(input))
                );
              }}
              value={values.cropId || undefined}
              onChange={(val?: string) => setFieldValue('cropId', val || '')}
              onBlur={() => setFieldTouched('cropId', true)}
              options={cropOptions}
              suffixIcon={
                isCropDropdownOpen ? (
                  <ChevronUp width={18} height={18} />
                ) : (
                  <ChevronDown width={18} height={18} />
                )
              }
              onDropdownVisibleChange={setIsCropDropdownOpen}
              getPopupContainer={getDropdownParentWithoutDetaching}
            />
          )
        }
      ]
    }
  ];

  return <FormGrid rows={planInfoRows} showErrorLineConditionally />;
};

export default PlanInfoSection;
